import { useEffect, useState, RefObject } from 'react';
import { Message } from '../types';

// Auto-scroll-to-latest and the "scroll to latest" button's visibility.
// chatMessagesRef/messagesEndRef are created in Chat.tsx (not here) since
// both are shared with other concerns — chatMessagesRef with history
// pagination's scroll-to-top listener, messagesEndRef with history
// pagination's initial-load scroll and the button's own onClick.
export const useChatScrollBehavior = (
  chatMessagesRef: RefObject<HTMLDivElement | null>,
  messagesEndRef: RefObject<HTMLDivElement | null>,
  messages: Message[],
  isLoadingHistory: boolean,
) => {
  // Shows the "scroll to latest" button once the user has scrolled away
  // from the bottom of the chat.
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // ── Auto-scroll to the latest message ────────────────────────────────────
  // Skipped while history is loading to avoid fighting with the scroll
  // restore logic in loadHistoryPage (which preserves scroll position).
  useEffect(() => {
    if (!isLoadingHistory) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ── Scroll-to-bottom button visibility ────────────────────────────────────
  // Shows the button as soon as the user scrolls up even a little from the
  // latest message, e.g. to reread earlier text while a response streams.
  useEffect(() => {
    const container = chatMessagesRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      setShowScrollToBottom(distanceFromBottom > 24);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return { showScrollToBottom, scrollToBottom };
};
